import React, { ReactNode } from 'react';
import {
  Box,
  Popover,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  Portal,
  Text,
  HStack,
  VStack,
  Link,
  PopoverAnchor,
  useDisclosure,
} from '@chakra-ui/react';
import { i18n } from '@investigator/shared/localization';
import { TruncatedText } from '@investigator/shared/components';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { ChatGPTIcon } from '../ChatGPTIcon';
import formatUnixTimeStampAsDisplayString from '@investigator/shared/utils/formatUnixTimeStampAsDisplayString';
import { useParseBackticks } from '@investigator/shared/hooks';

interface AIInsightsPopoverProps {
  description: string;
  entityName: string;
  lastUpdated: number | string;
  onViewAnalysis: () => void;
  children: ReactNode;
}

export const AIInsightsPopover: React.FC<AIInsightsPopoverProps> = ({
  entityName,
  lastUpdated,
  description,
  onViewAnalysis,
  children,
}): JSX.Element => {
  const { parseBackticks } = useParseBackticks();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const popoverLocales =
    i18n.pages.entities.highestRiskEntitiesTable.aiInsightsPopover;

  const handleViewAnalysisClick = () => {
    onClose();
    onViewAnalysis();
  };

  return (
    <Popover placement="bottom-start" isOpen={isOpen} onClose={onClose}>
      <PopoverAnchor>
        <Box
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onToggle();
          }}
          cursor="pointer"
          display="inline-block"
          position="relative"
          zIndex={10}
          userSelect={'none'}
        >
          {children}
        </Box>
      </PopoverAnchor>
      <Portal>
        <PopoverContent
          w="400px"
          borderColor="layer.3"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverArrow />
          <PopoverCloseButton w={5} h={5} fontSize="xs" right={2} top={2} />
          <PopoverBody p="15px">
            <VStack align="stretch" spacing={3}>
              <HStack spacing={3} align="center">
                <ChatGPTIcon boxSize={7} flexShrink={0} />
                <VStack
                  align="flex-start"
                  spacing={1}
                  flex={1}
                  justify="center"
                >
                  <Text fontSize="lg" fontWeight="semibold">
                    {entityName} {popoverLocales.title}
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {popoverLocales.lastUpdated}{' '}
                    {lastUpdated !== MISSING_DATA_PLACEHOLDER
                      ? formatUnixTimeStampAsDisplayString(
                          lastUpdated as number,
                        )
                      : MISSING_DATA_PLACEHOLDER}
                  </Text>
                </VStack>
              </HStack>
              <TruncatedText maxLines={2} fontSize="md" lineHeight="1.5">
                {parseBackticks(description)}
              </TruncatedText>
              <Link
                color="text.link"
                fontWeight="medium"
                _hover={{ textDecoration: 'underline' }}
                cursor="pointer"
                onClick={handleViewAnalysisClick}
              >
                {popoverLocales.viewAnalysis} &#8594;
              </Link>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
